import { useState } from 'react'
import { Box, Typography } from '@mui/material'
import { Gavel } from '@mui/icons-material'

/** Đường dẫn static (Vite `public/docs/`). */
const UEL_LOGO_SRC = '/docs/uel_logo_2026-01.png'
const TBS_LOGO_SRC = '/docs/logo-tbs.png'

export const BRAND_NAME = 'TBS UEL E-court'

interface BrandLogoMarksProps {
  /** Chiều cao logo (px). */
  height: number
  /** Nền sidebar tối: thêm nền sáng nhẹ để logo TBS (xanh lá) đọc rõ. */
  onDark?: boolean
  /** Sidebar hẹp: giảm chồng lệch để gọn chiều ngang. */
  tightOverlap?: boolean
}

/** Hai logo UEL + TBS chồng lệch (lồng) nhau theo trục ngang. */
function BrandLogoMarks({ height, onDark, tightOverlap }: BrandLogoMarksProps) {
  const [uelErr, setUelErr] = useState(false)
  const [tbsErr, setTbsErr] = useState(false)

  if (uelErr && tbsErr) {
    return (
      <Box
        sx={{
          width: height,
          height,
          borderRadius: 2,
          bgcolor: onDark ? 'rgba(255,255,255,0.2)' : 'primary.main',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        <Gavel sx={{ color: 'white', fontSize: height * 0.55 }} />
      </Box>
    )
  }

  const inner = (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        position: 'relative',
        height,
        flexShrink: 0,
      }}
      aria-hidden
    >
      {!uelErr && (
        <Box
          component="img"
          src={UEL_LOGO_SRC}
          alt=""
          onError={() => setUelErr(true)}
          sx={{ height, width: 'auto', display: 'block', zIndex: 1, position: 'relative' }}
        />
      )}
      {!tbsErr && (
        <Box
          component="img"
          src={TBS_LOGO_SRC}
          alt=""
          onError={() => setTbsErr(true)}
          sx={{
            height: height * (tightOverlap ? 0.88 : 0.92),
            width: 'auto',
            ml: tightOverlap ? -0.75 : -1.25,
            zIndex: 2,
            position: 'relative',
            alignSelf: 'center',
          }}
        />
      )}
    </Box>
  )

  if (!onDark) return inner

  return (
    <Box
      sx={{
        px: 0.75,
        py: 0.35,
        borderRadius: 2,
        bgcolor: 'rgba(255,255,255,0.96)',
        display: 'flex',
        alignItems: 'center',
        boxShadow: '0 1px 4px rgba(0,0,0,0.12)',
      }}
    >
      {inner}
    </Box>
  )
}

interface LogoProps {
  variant?: 'sidebar' | 'header' | 'compact' | 'topbar'
  showText?: boolean
}

export default function Logo({ variant = 'sidebar', showText = true }: LogoProps) {
  if (variant === 'compact' || variant === 'topbar') {
    const onDark = variant === 'topbar'
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.25,
          minWidth: 0,
          flexWrap: 'wrap',
          rowGap: 0.75,
        }}
      >
        <BrandLogoMarks height={40} onDark={onDark} tightOverlap />
        {showText && (
          <Typography
            variant="h6"
            sx={{
              fontWeight: 700,
              color: onDark ? '#fff' : 'primary.main',
              fontSize: onDark ? '0.95rem' : undefined,
              lineHeight: 1.25,
              wordBreak: 'break-word',
              minWidth: 0,
              flex: { xs: '1 1 140px', sm: '0 1 auto' },
            }}
          >
            {BRAND_NAME}
          </Typography>
        )}
      </Box>
    )
  }

  if (variant === 'header') {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: 'center',
          gap: { xs: 1, sm: 1.5 },
          textAlign: { xs: 'center', sm: 'left' },
          width: '100%',
        }}
      >
        <BrandLogoMarks height={36} onDark={false} tightOverlap />
        {showText && (
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2, color: 'primary.main', wordBreak: 'break-word' }}>
              {BRAND_NAME}
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.7rem', fontWeight: 500, display: 'block', mt: 0.25 }}>
              Tòa án điện tử thực nghiệm
            </Typography>
          </Box>
        )}
      </Box>
    )
  }

  /* Sidebar (~220px): xếp dọc — tránh logo 2 lớp chiếm hết chiều ngang làm mất chữ */
  return (
    <Box
      sx={{
        px: 1.75,
        py: 2,
        bgcolor: 'transparent',
        color: 'rgba(255, 255, 255, 0.95)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 1.25,
        textAlign: 'center',
      }}
    >
      <BrandLogoMarks height={36} onDark={true} tightOverlap />
      {showText && (
        <Box sx={{ width: '100%' }}>
          <Typography
            component="div"
            variant="subtitle2"
            sx={{
              fontWeight: 700,
              color: 'white',
              lineHeight: 1.35,
              fontSize: '0.8125rem',
              wordBreak: 'break-word',
              hyphens: 'auto',
            }}
          >
            {BRAND_NAME}
          </Typography>
          <Typography
            variant="caption"
            component="div"
            sx={{
              color: 'rgba(255, 255, 255, 0.88)',
              fontWeight: 600,
              letterSpacing: 0.06,
              fontSize: '0.625rem',
              textTransform: 'uppercase',
              mt: 0.35,
              lineHeight: 1.3,
            }}
          >
            Tòa án điện tử
          </Typography>
        </Box>
      )}
    </Box>
  )
}
